import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, ButtonBase, Checkbox, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Stack, TextField, Typography, useMediaQuery } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { ConcreteGrade } from '../pages/OfficeEstimationPage';
import {
  buildDayLedger, laborDestinationOptions, matchesRow, overIssueWarning, searchStockDestinations, stockBalance,
  suggestedRatioIssues, totalIssued, totalReceived, trackedMaterials, validateStockIssue,
  type StockDestination, type StockIssue, type StockOrderInput, type TrackedMaterial,
} from '../office/materialStock';
import { BORDER, DARK, GOLD, GREEN, MUTED, OutlineButton, PANEL, Pill, PrimaryButton, RED, ScreenBar, ScreenFooter, WARN } from './phoneUi';

const fmt = (value: number) => Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });
/** A quantity with its unit shown smaller and greyed out right after it. With `hideZero`, an amount of
 * exactly zero is left blank (keeping its layout space) instead of printing a noisy "0"/"+0"/"-0" — used
 * for the day-by-day ledger cells, where most days have nothing on one side or the other. */
const Qty = ({ amount, unit, prefix = '', hideZero, sx }: { amount: number; unit: string; prefix?: string; hideZero?: boolean; sx?: object }) => (
  <Box sx={sx}>{!(hideZero && amount === 0) && <Typography component="span">{prefix}{fmt(amount)} <Typography component="span" sx={{ fontSize: '0.78em', color: MUTED, fontWeight: 400 }}>{unit}</Typography></Typography>}</Box>
);
const fmtDate = (value?: string) => { if (!value) return ''; const d = new Date(`${value}T00:00:00`); return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); };
const today = () => new Date().toISOString().slice(0, 10);

/**
 * A running stock ledger for bulk consumables (cement, sand, aggregate) drawn from one common pile
 * across the project, rather than bought per activity — separate from, and built before, Allocation.
 * Usage is recorded per material: pick which labour trade used it (from the Rates tab's labour
 * items flagged to show here), a quantity, and an optional note.
 */
export default function StockScreen({ materialItems, orders, issues, concreteGrades, onSaveIssues, onDeleteIssue }: {
  materialItems: Parameters<typeof trackedMaterials>[0] & Parameters<typeof laborDestinationOptions>[0];
  orders: StockOrderInput[];
  issues: StockIssue[];
  concreteGrades: ConcreteGrade[];
  onSaveIssues: (issues: Omit<StockIssue, 'id'>[]) => Promise<void>;
  onDeleteIssue: (issue: StockIssue) => void;
}) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [openMaterialId, setOpenMaterialId] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);

  const materials = useMemo(() => trackedMaterials(materialItems), [materialItems]);
  const destinations = useMemo(() => laborDestinationOptions(materialItems), [materialItems]);
  const openMaterial = materials.find((material) => material.id === openMaterialId) || null;

  const listContent = <Stack gap={1.5}>
    <ScreenBar title="Site stock" sub="What's on site right now for bulk material drawn from one common pile — cement, sand, aggregate — and which labour trade has been taking it." />
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
      {materials.map((material) => {
        const balance = stockBalance(orders, issues, material.rootId, material.assemblyType);
        return <ButtonBase key={material.id} onClick={() => { setOpenMaterialId(material.id); setRecording(false); }} sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.25, textAlign: 'left', justifyContent: 'flex-start', flexWrap: 'wrap',
          px: 1.75, py: 1.25, borderTop: `1px solid ${BORDER}`, '&:first-of-type': { borderTop: 0 }, '&:active': { bgcolor: PANEL },
          ...(isDesktop && openMaterial?.id === material.id ? { bgcolor: PANEL } : {}),
        }}>
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Typography sx={{ fontWeight: 600, color: DARK, fontSize: 14 }}>{material.label}</Typography>
              <Pill tone={material.precision === 'tentative' ? 'warn' : 'ok'}>{material.precision === 'tentative' ? 'Tentative' : 'Exact'}</Pill>
            </Stack>
            <Typography sx={{ color: MUTED, fontSize: 12 }}>{fmt(totalReceived(orders, material.rootId, material.assemblyType))} received · {fmt(totalIssued(issues, material.rootId, material.assemblyType))} issued</Typography>
          </Box>
          <Qty amount={balance} unit={material.unit} sx={{ fontWeight: 700, fontSize: 15, color: balance < 0 ? RED : DARK }} />
        </ButtonBase>;
      })}
      {!materials.length && <Typography sx={{ p: 2.5, color: MUTED, fontSize: 13 }}>No material is marked to track as site stock yet. Turn it on for a material in Rates.</Typography>}
    </Box>
  </Stack>;

  const detailContent = !openMaterial ? null : recording
    ? <RecordIssueScreen
      material={openMaterial}
      balance={stockBalance(orders, issues, openMaterial.rootId, openMaterial.assemblyType)}
      destinations={destinations}
      concreteGrades={concreteGrades}
      trackedMaterials={materials}
      onBack={() => setRecording(false)}
      onSave={async (issuesToSave) => { await onSaveIssues(issuesToSave); setRecording(false); }}
    />
    : <MaterialDetailScreen
      material={openMaterial}
      orders={orders}
      issues={issues.filter((issue) => issue.materialId === openMaterial.rootId && matchesRow(issue.materialAssemblyType, openMaterial.assemblyType))}
      onBack={() => setOpenMaterialId(null)}
      onRecord={() => setRecording(true)}
      onDeleteIssue={onDeleteIssue}
    />;

  if (!isDesktop) return detailContent || listContent;

  return <Stack direction="row" gap={2} alignItems="flex-start">
    <Box sx={{ flex: '1 1 380px', maxWidth: 460 }}>{listContent}</Box>
    <Box sx={{ flex: '1 1 420px', position: 'sticky', top: 16, alignSelf: 'flex-start', minHeight: 420, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', borderRadius: '15px', bgcolor: detailContent ? '#fff' : PANEL, border: detailContent ? `1px solid ${BORDER}` : 'none' }}>
      {detailContent || <Stack sx={{ height: '100%', minHeight: 420, alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 4 }}>
        <Typography sx={{ color: MUTED, fontSize: 15 }}>Pick a material on the left to see its ledger or record usage.</Typography>
      </Stack>}
    </Box>
  </Stack>;
}

function MaterialDetailScreen({ material, orders, issues, onBack, onRecord, onDeleteIssue }: {
  material: TrackedMaterial;
  orders: StockOrderInput[];
  issues: StockIssue[];
  onBack: () => void;
  onRecord: () => void;
  onDeleteIssue: (issue: StockIssue) => void;
}) {
  const balance = stockBalance(orders, issues, material.rootId, material.assemblyType);
  const ledger = useMemo(() => [...buildDayLedger(orders, issues, material.rootId, material.assemblyType)].reverse(), [orders, issues, material.rootId, material.assemblyType]);
  const issuesByDate = useMemo(() => {
    const map = new Map<string, StockIssue[]>();
    for (const issue of issues) map.set(issue.date, [...(map.get(issue.date) || []), issue]);
    return map;
  }, [issues]);
  const [openDate, setOpenDate] = useState<string | null>(null);

  return <Stack sx={{ minHeight: 0 }}>
    <ScreenBar title={material.label} sub={`${fmt(totalReceived(orders, material.rootId, material.assemblyType))} ${material.unit} received to date`} onBack={onBack} />
    <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 1.75, py: 1.25, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 12, color: MUTED, textTransform: 'uppercase', fontWeight: 700 }}>On site now</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: balance < 0 ? RED : DARK }}>{fmt(balance)} <Typography component="span" sx={{ fontSize: 14, color: MUTED }}>{material.unit}</Typography></Typography>
      </Box>
      <PrimaryButton onClick={onRecord}>Record stock usage</PrimaryButton>
    </Stack>

    <Stack sx={{ px: 1.75, py: 1.5 }} gap={1.5}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: DARK, mb: 0.75 }}>Day by day</Typography>
        {ledger.length ? <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', overflow: 'hidden', bgcolor: '#fff' }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ px: 1.5, py: 0.75, bgcolor: PANEL, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ flex: '1 1 100px', fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>Date</Typography>
            <Typography sx={{ flex: '1 1 80px', fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', textAlign: 'right' }}>Opening</Typography>
            <Typography sx={{ flex: '1 1 80px', fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', textAlign: 'right' }}>Received</Typography>
            <Typography sx={{ flex: '1 1 80px', fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', textAlign: 'right' }}>Issued</Typography>
            <Typography sx={{ flex: '1 1 90px', fontSize: 10.5, fontWeight: 700, color: MUTED, textTransform: 'uppercase', textAlign: 'right' }}>Closing</Typography>
            <Box sx={{ width: 20 }} />
          </Stack>
          {ledger.map((day) => {
            const dayIssues = issuesByDate.get(day.date) || [];
            const expanded = openDate === day.date;
            return <Box key={day.date} sx={{ borderTop: `1px solid ${BORDER}`, '&:first-of-type': { borderTop: 0 } }}>
              <ButtonBase
                onClick={() => dayIssues.length && setOpenDate(expanded ? null : day.date)}
                sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, textAlign: 'left', justifyContent: 'flex-start', cursor: dayIssues.length ? 'pointer' : 'default' }}
              >
                <Typography sx={{ flex: '1 1 100px', fontSize: 12.5, fontWeight: 600, color: DARK }}>{fmtDate(day.date)}</Typography>
                <Qty amount={day.opening} unit={material.unit} hideZero sx={{ flex: '1 1 80px', fontSize: 12, color: MUTED, textAlign: 'right' }} />
                <Qty amount={day.received} prefix="+" unit={material.unit} hideZero sx={{ flex: '1 1 80px', fontSize: 12, color: GREEN, textAlign: 'right' }} />
                <Qty amount={day.issued} prefix="-" unit={material.unit} hideZero sx={{ flex: '1 1 80px', fontSize: 12, color: WARN, textAlign: 'right' }} />
                <Qty amount={day.closing} unit={material.unit} sx={{ flex: '1 1 90px', fontSize: 12.5, fontWeight: 700, color: DARK, textAlign: 'right' }} />
                <Box sx={{ width: 20, display: 'flex', justifyContent: 'flex-end' }}>
                  {!!dayIssues.length && (expanded
                    ? <KeyboardArrowUpIcon fontSize="small" sx={{ color: MUTED }} />
                    : <KeyboardArrowDownIcon fontSize="small" sx={{ color: MUTED }} />)}
                </Box>
              </ButtonBase>
              {expanded && <Box sx={{ bgcolor: PANEL, borderTop: `1px solid ${BORDER}` }}>
                {dayIssues.map((issue) => <Stack key={issue.id} direction="row" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ pl: 3, pr: 1.5, py: 1, borderTop: `1px solid ${BORDER}`, '&:first-of-type': { borderTop: 0 } }}>
                  <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, color: DARK, fontSize: 13.5 }}>{issue.destination.label}</Typography>
                    {issue.note && <Typography sx={{ color: MUTED, fontSize: 11.5 }}>{issue.note}</Typography>}
                  </Box>
                  <Qty amount={issue.qty} unit={issue.unit} sx={{ fontWeight: 700, fontSize: 13 }} />
                  <IconButton size="small" aria-label={`Remove issue of ${issue.materialLabel}`} onClick={() => onDeleteIssue(issue)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Stack>)}
              </Box>}
            </Box>;
          })}
        </Box> : <Typography sx={{ color: MUTED, fontSize: 13 }}>No receipts or issues recorded yet.</Typography>}
      </Box>
    </Stack>
  </Stack>;
}

function RecordIssueScreen({ material, balance, destinations, concreteGrades, trackedMaterials: allTracked, onBack, onSave }: {
  material: TrackedMaterial;
  balance: number;
  destinations: StockDestination[];
  concreteGrades: ConcreteGrade[];
  trackedMaterials: TrackedMaterial[];
  onBack: () => void;
  onSave: (issues: Omit<StockIssue, 'id'>[]) => Promise<void>;
}) {
  const [qty, setQty] = useState('');
  const [destination, setDestination] = useState<StockDestination | null>(null);
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isCement = material.group === 'cement';
  const [isConcrete, setIsConcrete] = useState(false);
  const [gradeId, setGradeId] = useState('');
  const [ratioQtys, setRatioQtys] = useState<Record<string, string>>({});
  const problems = validateStockIssue(qty, destination);
  const warning = overIssueWarning(qty, balance, material.unit);
  const grade = concreteGrades.find((g) => g.id === gradeId) || null;
  const suggested = grade ? suggestedRatioIssues(grade, Number(qty) || 0, allTracked) : [];

  const save = async () => {
    if (!destination) return;
    setSaving(true); setError('');
    try {
      const batchId = isConcrete ? `batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` : undefined;
      const primary: Omit<StockIssue, 'id'> = { materialId: material.rootId, materialAssemblyType: material.assemblyType, materialLabel: material.label, unit: material.unit, qty: Number(qty), date, destination, note: note.trim(), batchId };
      const extras: Omit<StockIssue, 'id'>[] = isConcrete
        ? suggested
          .filter((row) => Number(ratioQtys[row.key] ?? row.qty) > 0)
          .map((row) => ({ materialId: row.materialId, materialAssemblyType: row.materialAssemblyType, materialLabel: row.materialLabel, unit: row.unit, qty: Number(ratioQtys[row.key] ?? row.qty), date, destination, note: note.trim(), batchId }))
        : [];
      await onSave([primary, ...extras]);
    } catch (err: any) { setError(err?.message || 'Could not record this issue.'); setSaving(false); }
  };

  return <Stack sx={{ minHeight: 0 }}>
    <ScreenBar title={`Record stock usage · ${material.label}`} sub={`${fmt(balance)} ${material.unit} currently on site`} onBack={saving ? undefined : onBack} />
    <Stack gap={1.5} sx={{ px: 1.75, py: 1.5 }}>
      <Autocomplete
        options={destinations}
        getOptionLabel={(option) => option.label}
        filterOptions={(list, state) => searchStockDestinations(list, state.inputValue)}
        value={destination}
        onChange={(_, value) => setDestination(value)}
        renderInput={(params) => <TextField {...params} label="Labour used" placeholder="Search labour trades" helperText={!destinations.length ? 'No labour trade is set to show here yet — turn it on in Rates.' : undefined} inputProps={{ ...params.inputProps, 'aria-label': 'Labour used' }} />}
      />
      <Stack direction="row" gap={1} flexWrap="wrap">
        <TextField label={`Quantity (${material.unit})`} type="number" value={qty} onChange={(event) => setQty(event.target.value)} inputProps={{ min: 0, step: 'any', 'aria-label': 'Quantity' }} sx={{ flex: '1 1 160px' }} />
        <TextField label="Date" type="date" value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px' }} />
      </Stack>
      <TextField label="Note (optional)" value={note} onChange={(event) => setNote(event.target.value)} placeholder="e.g. RCC slab, 3rd floor" fullWidth />
      {warning && <Typography sx={{ color: WARN, fontSize: 12.5, fontWeight: 600 }}>{warning}</Typography>}
      {error && <Typography sx={{ color: 'error.main', fontSize: 13 }}>{error}</Typography>}

      {isCement && !!concreteGrades.length && <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '15px', p: 1.5, bgcolor: PANEL }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={isConcrete} onChange={(e) => setIsConcrete(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DARK }}>Is this for concrete?</Typography>}
        />
        {isConcrete && <Stack gap={1.25} mt={0.5}>
          <FormControl size="small" sx={{ maxWidth: 220 }}>
            <InputLabel id="concrete-grade-label">Grade</InputLabel>
            <Select labelId="concrete-grade-label" label="Grade" value={gradeId} onChange={(e) => { setGradeId(e.target.value); setRatioQtys({}); }}>
              {concreteGrades.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </Select>
          </FormControl>
          {grade && (suggested.length
            ? <Stack gap={1}>
              <Typography sx={{ fontSize: 12, color: MUTED }}>Suggested from {grade.name}'s ratio — edit or clear a row to skip it.</Typography>
              {suggested.map((row) => (
                <TextField
                  key={row.key}
                  label={`${row.materialLabel} (${row.unit})`}
                  type="number"
                  value={ratioQtys[row.key] ?? String(row.qty)}
                  onChange={(e) => setRatioQtys((previous) => ({ ...previous, [row.key]: e.target.value }))}
                  size="small"
                  sx={{ maxWidth: 280 }}
                />
              ))}
            </Stack>
            : <Typography sx={{ fontSize: 12, color: MUTED }}>This grade has no ratios set up yet — add them in Rates &gt; Manage concrete grades.</Typography>)}
        </Stack>}
      </Box>}
    </Stack>
    <ScreenFooter>
      {problems.length > 0 && <Typography sx={{ flex: 1, color: WARN, fontSize: 12 }}>To save: {problems.join('; ')}</Typography>}
      {!problems.length && <Box sx={{ flex: 1 }} />}
      <OutlineButton onClick={onBack} disabled={saving}>Cancel</OutlineButton>
      <PrimaryButton disabled={!!problems.length || saving} onClick={save} sx={{ bgcolor: GOLD }}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
    </ScreenFooter>
  </Stack>;
}
